import {ProductOptionType} from '@wix/wixstores-graphql-schema/dist/es/src/graphql-schema';
import classNames from 'classnames';
import React from 'react';
import type {BoxSelectionProps, DropdownOptionProps, DropdownProps} from 'wix-ui-tpa/cssVars';
import {BoxSelection, BoxSelectionBoxSize} from 'wix-ui-tpa/cssVars';
import {Experiments, ProductOptionWidthType, TextualOptionType} from '../../../../../constants';
import {useGlobals} from '../../../../../globalPropsContext';
import {IProduct} from '../../../../../types/galleryTypes';
import {LazyDropDown} from '../../../../lazyComponents/lazyWUTComponents';
import {classes as dropdownOptionStyles, style as styleDropdown} from '../DropdownOption.st.css';
import {DataHook, ProductOptionsChangeData} from '../ProductOptions';
import {classes as boxSelectionStyles} from './ProductTextualOption.st.css';
import s from './TextualOptions.scss';

type IProductOption = IProduct['options'][0];

export interface TextualOptionProps {
  option: IProductOption;
  availability: Record<string, {isVisible: boolean; isSelectable: boolean}>;
  selectionIds: number[];
  readOnly?: boolean;
  isLoading: boolean;
  onSelectionIdsChange: (optionSelections: number[], changeData: ProductOptionsChangeData) => Promise<void>;
}

export const ProductTextualOption: React.FC<TextualOptionProps> = ({
  option,
  availability,
  selectionIds,
  readOnly,
  isLoading,
  onSelectionIdsChange,
}) => {
  const {useExperiments, shouldShowMobile, styles, stylesParams} = useGlobals();
  const fitToText = styles.get(stylesParams.gallery_buttonOptionWidthType) === ProductOptionWidthType.FIT;
  const shouldUseNativeDropdownOnMobile = useExperiments.enabled(Experiments.GalleryProductOptionMobileNativeDropdown);
  const isButtonOptionsEnabled = useExperiments.enabled(Experiments.GalleryProductOptionsTextualOptionType);
  const textualOptionType = styles.get(stylesParams.gallery_textualOptionType);
  const useDropdownTpaLabel = useExperiments.enabled(Experiments.UseDropdownTpaLabel);

  const handleOptionChange = (optionsSelectionIds: number[], id: string) => {
    const nextSelectionIds = [
      ...selectionIds.filter((selectionId) => !optionsSelectionIds.includes(selectionId)),
      parseInt(id, 10),
    ];
    void onSelectionIdsChange(nextSelectionIds, {
      optionType: ProductOptionType.DROP_DOWN,
    });
  };

  const sealButtonChangeHandler = (optionsSelectionIds: number[]): BoxSelectionProps['onChange'] => {
    return ({id}) => {
      const parts = id.split('-');
      const selectionId = parts[parts.length - 1];
      handleOptionChange(optionsSelectionIds, selectionId);
    };
  };

  const sealDropdownChangeHandler =
    (optionsSelectionIds: number[]): DropdownProps['onChange'] =>
    ({id}) => {
      handleOptionChange(optionsSelectionIds, id);
    };

  const shouldAllowCustomProductOptionsAndQuantityWidth = useExperiments.enabled(
    Experiments.GalleryProductOptionsAndQuantityWidth
  );

  const {title, selections, id} = option;
  const options: DropdownOptionProps[] = selections
    .filter((selection) => availability[selection.id].isVisible)
    .map((selection) => ({
      id: selection.id.toString(),
      isSelectable: availability[selection.id].isSelectable,
      value: selection.description,
    }));

  const selectionIdsFromOption = selections.map((selection) => selection.id);

  return (
    <div
      data-hook={DataHook.ProductOption}
      className={classNames({[s.dropdownAlignment]: shouldAllowCustomProductOptionsAndQuantityWidth})}>
      {isButtonOptionsEnabled && textualOptionType === TextualOptionType.BUTTONS ? (
        <BoxSelection
          name={`button-option-${id}`}
          aria-label={title}
          size={BoxSelectionBoxSize.xSmall}
          onChange={sealButtonChangeHandler(selectionIdsFromOption)}
          data-hook={DataHook.ButtonOption}
          className={classNames(s.buttonOption, {
            [s.fitToText]: fitToText,
          })}>
          {options.map((selection, index) => (
            <BoxSelection.Option
              key={selection.id}
              id={`${option.id}-${selection.id}`}
              unstableHasInnerWrapper={false}
              checked={selectionIds.includes(parseInt(selection.id, 10))}
              aria-label={selection.value}
              disabled={!selection.isSelectable}
              data-hook={`box-selection-option-${index}`}
              className={classNames(boxSelectionStyles.buttonOptionItem, {
                [s.flexGrow]: fitToText,
              })}>
              <span
                data-hook={`box-selection-text-option-${index}`}
                className={classNames(s.optionItemText, {
                  [s.strikethrough]: !selection.isSelectable,
                })}>
                {selection.value}
              </span>
            </BoxSelection.Option>
          ))}
        </BoxSelection>
      ) : (
        <LazyDropDown
          className={
            /* istanbul ignore next */
            classNames(styleDropdown(dropdownOptionStyles.dropdownOption), {
              [s.dropdownWidth]: shouldAllowCustomProductOptionsAndQuantityWidth,
              [s.isMobile]: shouldAllowCustomProductOptionsAndQuantityWidth && shouldShowMobile,
            })
          }
          data-hook={DataHook.DropdownOption}
          aria-label={useDropdownTpaLabel ? title : ''}
          placeholder={title}
          options={options}
          onChange={sealDropdownChangeHandler(selectionIdsFromOption)}
          isLoading={isLoading}
          readOnly={readOnly}
          mobileNativeSelect={shouldUseNativeDropdownOnMobile && shouldShowMobile}
        />
      )}
    </div>
  );
};
